
import { useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContainer } from "@/components/auth/AuthContainer";
import { StudioMenu } from "@/components/studio/StudioMenu";
import { useCanvasSetup } from "@/hooks/use-canvas-setup";
import { useMeasurementTools } from "@/hooks/use-measurement-tools";
import { useCanvasEvents } from "@/hooks/use-canvas-events";
import { Feedback } from "@/components/feedback/Feedback";
import { useCanvasState } from "@/hooks/use-canvas-state";
import { useUIState } from "@/hooks/use-ui-state";
import { StudioLayout } from "@/components/studio/StudioLayout";
import { StudioDialogs } from "@/components/studio/StudioDialogs";
import { useStudioSetup } from "@/hooks/use-studio-setup";
import { useDrawingHandlers } from "@/hooks/use-drawing-handlers";
import { useAnalysis } from "@/hooks/use-analysis";
import { useKeyboardShortcuts } from "@/hooks/use-keyboard-shortcuts";
import { useLayerManagement } from "@/hooks/use-layer-management";

export const Studio = () => {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const resetAnalysisRef = useRef<(() => void) | null>(null);

  const {
    activeTool,
    setActiveTool,
    brushSize,
    isCanvasEmpty,
    setIsCanvasEmpty,
    projectTitle,
    setProjectTitle,
    currentDrawingId,
    setCurrentDrawingId,
  } = useCanvasState();

  const {
    isToolbarVisible,
    setIsToolbarVisible,
    showSampleImages,
    setShowSampleImages,
    showLayersSidebar,
    setShowLayersSidebar,
    showDrawingHistory,
    setShowDrawingHistory,
    showPricing,
    setShowPricing,
    refreshDrawings,
    refreshDrawingsCounter,
  } = useUIState();

  useEffect(() => {
    if (activeTool === "add" && !showLayersSidebar) {
      setShowLayersSidebar(true);
    }
  }, [activeTool, showLayersSidebar, setShowLayersSidebar]);

  const handleLayersSidebarClose = useCallback(() => {
    setShowLayersSidebar(false);
    if (activeTool === "add") {
      setActiveTool(null);
    }
  }, [activeTool, setActiveTool, setShowLayersSidebar]);

  const { canvas, clearCanvas } = useCanvasSetup({
    canvasRef,
    activeTool,
    brushSize,
    setCurrentDrawingId,
    setProjectTitle,
    setActiveTool,
  });

  const layerManagement = useLayerManagement(canvas);
  
  const { resetAnalysis } = useAnalysis(canvas, currentDrawingId);

  const handleResetAnalysis = useCallback((resetFn: () => void) => {
    console.log('Received resetAnalysis function from SecondOpinion');
    resetAnalysisRef.current = resetFn;
  }, []);

  const resetAnalysisFromRef = useCallback(() => {
    if (resetAnalysisRef.current) {
      console.log('Calling resetAnalysis from ref');
      resetAnalysisRef.current();
    } else {
      console.log('resetAnalysisRef is not set yet, using local resetAnalysis');
      resetAnalysis();
    }
  }, [resetAnalysis]);

  const { rawMode, handleRawModeChange } = useStudioSetup(canvas, setIsCanvasEmpty);

  const { handleCanvasClick } = useMeasurementTools({
    canvas,
    activeTool: activeTool as "ruler" | "angle" | null,
    currentZoom: 100
  });

  const { 
    hasSelection,
    handleLoadDrawing,
    undoLastAction,
    redoLastAction,
    deleteSelectedObjects,
    canUndo,
    canRedo
  } = useCanvasEvents({
    canvas,
    activeTool,
    onCurrentDrawingIdChange: setCurrentDrawingId,
    onResetAnalysis: resetAnalysisFromRef
  });

  const {
    handleDrawingLoad,
    handleClearCanvas,
    handleToolToggle,
  } = useDrawingHandlers({
    canvas,
    handleLoadDrawing,
    setCurrentDrawingId,
    setProjectTitle,
    clearCanvas,
    resetAnalysis: resetAnalysisFromRef, 
  });

  const handleClearCanvasWrapper = useCallback(() => {
    console.log('handleClearCanvasWrapper called, calling handleClearCanvas');
    handleClearCanvas();
  }, [handleClearCanvas]);

  useKeyboardShortcuts({
    canvas,
    deleteSelectedObjects,
    isEnabled: !showPricing,
    canUndo,
    canRedo,
    onUndo: undoLastAction,
    onRedo: redoLastAction
  });

  return (
    <div className="h-screen flex flex-col relative w-full overflow-visible">
      <AuthContainer showPricing={showPricing} onShowPricing={setShowPricing} />
      <StudioMenu 
        onPricingClick={() => setShowPricing(true)}
        onLoadDrawing={handleLoadDrawing}
        setIsToolbarVisible={setIsToolbarVisible}
        currentTitle={projectTitle}
        onClear={handleClearCanvasWrapper}
        canvas={canvas} // Pass the canvas prop to StudioMenu
      />
      
      <StudioLayout
        canvasRef={canvasRef}
        canvas={canvas}
        activeTool={activeTool}
        isCanvasEmpty={isCanvasEmpty}
        showPricing={showPricing}
        isToolbarVisible={isToolbarVisible}
        projectTitle={projectTitle}
        currentDrawingId={currentDrawingId}
        hasSelection={hasSelection}
        onToolToggle={(tool) => setActiveTool(handleToolToggle(tool))}
        onUndo={undoLastAction}
        onRedo={redoLastAction}
        onDelete={deleteSelectedObjects}
        onClear={handleClearCanvasWrapper}
        onTitleChange={setProjectTitle}
        onDrawingSaved={setCurrentDrawingId}
        setIsToolbarVisible={setIsToolbarVisible}
        onRefreshDrawings={refreshDrawings}
        rawMode={rawMode}
        onRawModeChange={handleRawModeChange}
        canUndo={canUndo}
        canRedo={canRedo}
        onResetAnalysis={resetAnalysisFromRef}
        layerManagement={layerManagement}
      />

      <Feedback showPricing={showPricing} />
      
      <StudioDialogs
        canvas={canvas}
        showSampleImages={showSampleImages}
        showLayersSidebar={showLayersSidebar}
        showDrawingHistory={showDrawingHistory}
        showPricing={showPricing}
        onSampleImagesClose={() => setShowSampleImages(false)}
        onLayersSidebarClose={handleLayersSidebarClose}
        onDrawingHistoryChange={setShowDrawingHistory}
        onPricingClose={() => setShowPricing(false)}
        onToolChange={(tool) => setActiveTool(handleToolToggle(tool))}
        onLoadDrawing={handleDrawingLoad}
        onRefreshDrawings={refreshDrawings}
        onGetStarted={() => {
          setShowPricing(false);
          navigate("/auth");
        }}
        onResetAnalysis={handleResetAnalysis}
        layerManagement={layerManagement}
      />
    </div>
  );
};
